import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { AuthContext } from "~/auth/AuthProvider";
import quoteTourOrdersQuery from "~/helpers/quoteTourOrdersQuery";
import { useAppSelector } from "~/hooks/reduxHooks";
import useStartTour from "~/hooks/useStartTour";
import { useGetOrdersQuery } from "~/services/endpoints/apiEndpoints";
import { getWalkthrough, matchesWalkthroughRoute } from "~/walkthroughs/registry";
import { isBpfTenant } from "~fulfill/orders/helpers/invoiceEligibility";

export interface UseLaunchTourReturn {
	canLaunch: (tourId?: string | null) => boolean;
	launch: (tourId: string, itemId: string) => void;
}
const useLaunchTour = (tourIds: string[]): UseLaunchTourReturn => {
	const location = useLocation();
	const navigate = useNavigate();
	const startTour = useStartTour();
	const { wsapiTenant } = useContext(AuthContext);
	const email = useAppSelector((state) => state.settings.email);
	const [pending, setPending] = useState<{
		tourId: string;
		path: string;
		navigating: boolean;
		originPath: string;
	} | null>(null);
	const needsQuoteRecord =
		location.pathname === "/" &&
		tourIds.includes("quoteFromOpenOrder") &&
		Boolean(getWalkthrough("quoteFromOpenOrder")) &&
		!isBpfTenant(wsapiTenant) &&
		Boolean(email);
	const { data, isError } = useGetOrdersQuery(quoteTourOrdersQuery(email), { skip: !needsQuoteRecord });
	const quoteOrderId = !isError ? data?.orders?.nodes?.[0]?.orderId : undefined;
	const recordResolvers: Record<string, string | undefined> = {
		quoteFromOpenOrder: needsQuoteRecord && quoteOrderId ? `/fulfill/orders/order/${quoteOrderId}` : undefined
	};
	const resolvePath = (tourId: string): string | undefined => {
		const definition = getWalkthrough(tourId);
		if (!definition) return undefined;
		return definition.routePath.includes(":") ? recordResolvers[tourId] : definition.routePath;
	};
	useEffect(() => {
		if (!pending) return;
		const definition = getWalkthrough(pending.tourId);
		if (!definition) {
			setPending(null);
			return;
		}
		if (!pending.navigating) {
			setPending({ ...pending, navigating: true });
			navigate(pending.path);
		} else if (matchesWalkthroughRoute(location.pathname, definition.routePath)) {
			setPending(null);
			startTour(pending.tourId, undefined, true);
		} else if (location.pathname !== pending.originPath) {
			setPending(null);
		}
	}, [pending, location.pathname, navigate, startTour]);
	return {
		canLaunch: (tourId) => Boolean(tourId && resolvePath(tourId)),
		launch: (tourId, itemId) => {
			const path = resolvePath(tourId);
			if (!path) return;
			navigate(location.pathname + location.search, {
				replace: true,
				state: { ...location.state, whatsNewFocus: itemId }
			});
			setPending({ tourId, path, navigating: false, originPath: location.pathname });
		}
	};
};
export default useLaunchTour;
